import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Play, ShieldCheck, Zap } from "lucide-react";
import { SITE, waLink, IMAGES } from "../data/site";

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <span style={{ animationDelay: `${delay}s` }}>{children}</span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section id="conteudo" ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden grain pt-[72px]" data-testid="hero-section">
      {/* background layers */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
        <img src={IMAGES.heroSolar} alt="Usina solar ao pôr do sol" className="w-full h-[120%] object-cover opacity-35" />
        <div className="absolute inset-0 bg-[#04120E]/70" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-[#04120E]" style={{ maskImage: "linear-gradient(to top, black, transparent)" }} />
      </motion.div>
      <motion.div style={{ opacity: glowOpacity }} className="absolute -z-10 top-1/4 -left-40 w-[560px] h-[560px] rounded-full bg-[#00E676]/15 blur-[140px]" />
      <div className="absolute -z-10 bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#A3E635]/10 blur-[120px]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/30 bg-[#00E676]/10 px-4 py-1.5 text-xs font-mono2 uppercase tracking-[0.2em] text-[#00E676] mb-8"
            data-testid="hero-badge"
          >
            <Zap size={13} /> Licenciado Autorizado iGreen Energy
          </motion.p>

          <h1 className="font-heading font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-[4.4rem] text-white" data-testid="hero-headline">
            <MaskLine delay={0.25}>Pague menos</MaskLine>
            <MaskLine delay={0.4}>
              <span className="text-[#00E676] text-glow">na conta de luz.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-emerald-100/75"
            data-testid="hero-subheadline"
          >
            <strong className="text-white">Sem obra e sem investimento:</strong> energia solar de usinas parceiras
            com <strong className="text-[#00E676]">desconto de até 15% na conta de luz</strong>, conforme a sua região.
            E ainda celular 5G e seguro auto com quem te atende de perto, pelo WhatsApp.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#simular"
              data-testid="hero-calc-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C853] text-[#04120E] font-bold px-7 py-4 shadow-[0_0_28px_rgba(0,230,118,0.45)] transition-[background-color,transform] duration-300 hover:scale-[1.03]"
            >
              Calcular minha economia <ArrowRight size={18} />
            </a>
            <a
              href="#conectar"
              data-testid="hero-connect-cta"
              className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/35 bg-emerald-950/50 backdrop-blur-sm text-[#00E676] font-semibold px-7 py-4 hover:bg-emerald-900/60 transition-[background-color,transform] duration-300 hover:scale-[1.03]"
            >
              Conectar grátis em 5 minutos
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-emerald-100/55"
            data-testid="hero-trust-pills"
          >
            {["Sem obra", "Sem custo de adesão", "Você continua recebendo energia pela mesma rede"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-[#00E676]" /> {t}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <div className="relative animate-float-slow">
            <div className="absolute -inset-3 rounded-3xl bg-[#00E676]/20 blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-[#00E676]/25 bg-[#0B261E]/80 backdrop-blur-md shadow-2xl" data-testid="hero-video-frame">
              <div className="aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${SITE.heroVideoId}?rel=0`}
                  title="Vídeo iGreen Energia"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="flex items-center gap-3 px-5 py-4 border-t border-white/10">
                <span className="grid place-items-center w-9 h-9 rounded-full bg-[#00E676]/15 text-[#00E676]">
                  <Play size={15} />
                </span>
                <p className="text-xs text-emerald-100/70 leading-snug">
                  Entenda em 1 minuto como a energia por assinatura reduz a sua conta.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
